<template>
  <v-container
    fluid
    pa-0
    fill-height
    class="align-start"
    style="max-width: 100%"
  >
    <div
      class="d-flex flex-grow-1 fill-height"
      v-if="selectedEventUUID && event && $store.get('event/stops').length"
    >
      <div :class="$style.scrollContainer">
        <HistoryEventInfo />
      </div>

      <transition name="slide-y-transition" mode="out-in">
        <OutputTabs />
      </transition>
    </div>
  </v-container>
</template>

<script>
import HistoryEventInfo from '@/modules/history/components/HistoryEventInfo'
import OutputTabs from '@/components/OutputTabs'
import gql from 'graphql-tag'
import { get } from 'vuex-pathify'

export default {
  name: 'HistoryEventContainer',

  components: {
    OutputTabs,
    HistoryEventInfo
  },

  computed: {
    selectedEventUUID: get('event/selectedUUID'),
    event: get('event/selectedEvent')
  },

  apollo: {
    event: {
      query: gql`
        query event($filter: GetEventInput!) {
          event(filter: $filter) {
            uuid
            title
            time_start
            comment
            basun
            location
            basun_time
            last_dua_message {
              uuid
              created
              body {
                trip_uuid
                data
                response_data
                status
              }
            }
            dua_messages {
              uuid
              created
              body {
                trip_uuid
                data
              }
            }
            traffic_messages {
              uuid
              created
              event_uuid
              subject
              routes
              delays
              replacements
              yellow_vests
              train_deviations
              turnarounds
              forecast_comment
              replacement_comment
              yellow_vest_comment
              consequence
            }
            last_sms_message {
              uuid
              event_uuid
              trips {
                trip_uuid
                data {
                  sms_text
                  reason_code
                  bookings {
                    first_name
                    last_name
                    phone_number
                    train_number
                    departure_date
                    time_start
                    time_end
                    place_start
                    place_end
                  }
                }
              }
            }
            sms_messages {
              uuid
              event_uuid
              trips {
                trip_uuid
                data {
                  bookings {
                    first_name
                    last_name
                    phone_number
                    train_number
                    departure_date
                    time_start
                    time_end
                    place_start
                    place_end
                  }
                }
              }
            }
            last_traffic_message {
              uuid
              created
              event_uuid
              subject
              routes
              delays
              replacements
              yellow_vests
              train_deviations
              turnarounds
              forecast_comment
              replacement_comment
              yellow_vest_comment
              consequence
            }
            routes {
              uuid
              from_place
              to_place
            }
            reason {
              name
              uuid
            }
            operation_level {
              uuid
              name
            }
            delays {
              event_uuid
              uuid
              train_number
              type
              location_place
              location_time
              delay
              checked
              deleted
            }
            replacements {
              event_uuid
              uuid
              type
              train_number
              from
              to
              via
              status
              checked
              deleted
            }
            train_deviations {
              event_uuid
              uuid
              type
              train_number
              from
              to
              departure
              arrival
              deviation_type
              checked
              places
              deleted
            }
            turnarounds {
              event_uuid
              uuid
              train_number_from
              train_number_to
              location
              checked
              deleted
            }
            yellow_vests {
              event_uuid
              uuid
              assigned
              demand
              location
              checked
              deleted
            }
          }
        }
      `,
      variables() {
        return {
          filter: {
            uuid: this.$route.params.event_uuid
          }
        }
      },
      manual: true,
      fetchPolicy: 'no-cache',
      async result({ data }) {
        const event_uuid = this.$route.params.event_uuid

        if (!data || !data.event || !event_uuid) {
          this.$store.dispatch('event/unSelectEvent')
          return
        }

        await this.$store.dispatch('event/mapData', [data.event])
        this.$store.dispatch(
          'trainDeviation/mapData',
          data.event?.train_deviations || null
        )
        this.$store.dispatch('delay/mapData', data.event?.delays || null)
        this.$store.dispatch(
          'replacement/mapData',
          data.event?.replacements || null
        )
        this.$store.dispatch(
          'turnaround/mapData',
          data.event?.turnarounds || null
        )
        this.$store.dispatch(
          'yellowVest/mapData',
          data.event?.yellow_vests || null
        )

        this.$store.set(
          'history/trafficMessages',
          data.event?.traffic_messages || null
        )
        this.$store.set('history/duaMessages', data.event?.dua_messages || null)

        this.$store.set(
          'history/selectedDuaMessageUUID',
          data.event?.last_dua_message?.uuid || null
        )
        this.$store.set(
          'history/selectedTrafficMessageUUID',
          data.event?.last_traffic_message?.uuid || null
        )

        this.$store.dispatch('duaMessage/setForm')

        if (event_uuid) {
          this.$store.dispatch('event/selectEvent', event_uuid)
        }
      }
    },
    stops: {
      query: gql`
        query stops {
          stops {
            stop_name
            short_name
          }
        }
      `,
      manual: true,
      fetchPolicy: 'no-cache',
      result({ data }) {
        if (!data) return
        this.$store.set('event/stops', data.stops)
      }
    }
  },

  beforeDestroy() {
    this.$store.set('event/selectedUUID', null)
    this.$store.commit('trafficMessage/resetModel')
    this.$store.commit('duaMessage/resetModel')
    this.$store.set('duaMessage/form', {})
  }
}
</script>

<style lang="scss" module>
.scrollContainer {
  overflow-y: auto;
  height: 100%;
  max-height: calc(100vh - 56px);
}
</style>
